import { AdvancedSearchEntityType, BridgeEntityResult } from "generated/graphql";
import React from "react";
import { useBridgeTypes } from "store/selectors/bridge";

function taskParentLabel(entityType: AdvancedSearchEntityType): string | null {
    switch (entityType) {
        case AdvancedSearchEntityType.Client:
            return "Clients";
        case AdvancedSearchEntityType.Community:
            return "Communities";
        case AdvancedSearchEntityType.ReferenceContact:
            return "Referral Contacts";
        case AdvancedSearchEntityType.ReferenceBusiness:
            return "Referral Organizations";
        case AdvancedSearchEntityType.HomeCareLead:
            return "Home Care Clients";
        case AdvancedSearchEntityType.CommunityContact:
        case AdvancedSearchEntityType.Activity:
        case AdvancedSearchEntityType.CalendarEvent:
        case AdvancedSearchEntityType.Invoice:
        case AdvancedSearchEntityType.Task:
        case AdvancedSearchEntityType.Tour:
        case AdvancedSearchEntityType.WorkflowExecution:
            return null;
        default: {
            const exhaustiveCheck: never = entityType;
            throw new Error(`Unhandled entity type: ${exhaustiveCheck}`);
        }
    }
}

export type TaskOwnerViewableKind = "ALL" | "UNOWNED" | AdvancedSearchEntityType;

export interface AnnotatedViewableTaskType {
    name: string;
    kind: TaskOwnerViewableKind;
}

function buildViewableTaskTypes(
    bridgeEntityTypes: BridgeEntityResult[],
): AnnotatedViewableTaskType[] {
    return [
        { name: "All Associations", kind: "ALL" },
        ...bridgeEntityTypes
            .filter(e => e.supportsTasks)
            .map(e => ({
                name: taskParentLabel(e.name as AdvancedSearchEntityType),
                kind: e.name as TaskOwnerViewableKind,
            }))
            .filter((x): x is AnnotatedViewableTaskType => x.name !== null),
    ];
}

export const useViewableTaskTypes = (): AnnotatedViewableTaskType[] => {
    const { entityTypes, status } = useBridgeTypes();
    return React.useMemo(
        () => (status === "ready" ? buildViewableTaskTypes(entityTypes) : []),
        [entityTypes, status],
    );
};
